/**
 * Notifications, on whichever channel this device has.
 *
 * The client half of the API's dispatch seam. Permission is asked IN CONTEXT — from a card the
 * user taps, right after pairing succeeds — never on load, on either platform.
 *
 *   web     the browser's PushManager against the app's own VAPID key. The payload that arrives is
 *           {module, count} and public/sw.js composes the words on the device.
 *   native  Expo's push token. Expo has no encrypted content-free mode, so the server composes
 *           the words instead — from a table that is a pure function of the module and the count.
 *
 * Both are content-free pings either way, which is the invariant; which side writes the sentence
 * is an implementation detail of the channel.
 */
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import { api } from './api';

const isWeb = Platform.OS === 'web';

const webSupported = () => isWeb && typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in globalThis;

/** Whether this device can be pinged at all. A simulator cannot, and says so rather than failing. */
export const pushSupported = (): boolean => (isWeb ? webSupported() : Device.isDevice);

export async function currentPermission(): Promise<'granted' | 'denied' | 'default' | 'unsupported'> {
  if (!pushSupported()) return 'unsupported';
  if (isWeb) return Notification.permission as 'granted' | 'denied' | 'default';
  const { status } = await Notifications.getPermissionsAsync();
  return status === 'granted' ? 'granted' : status === 'denied' ? 'denied' : 'default';
}

/** Whether THIS device is registered with the server (not merely permitted). */
export async function isSubscribed(): Promise<boolean> {
  if (!pushSupported()) return false;
  if (isWeb) {
    const reg = await navigator.serviceWorker.ready;
    return !!(await reg.pushManager.getSubscription());
  }
  return (await currentPermission()) === 'granted';
}

/** Returns true when the subscription reached the server. */
export async function enablePush(): Promise<boolean> {
  if (!pushSupported()) return false;
  const registered = isWeb ? await subscribeWeb() : await subscribeNative();
  if (!registered) return false;

  // Prove the loop end to end: the other partner's device shows a content-free ping.
  await api.post('/api/push/pairing-complete').catch(() => undefined);
  return true;
}

/** Home-panel button: ping the other partner manually. */
export async function sendTestPush(): Promise<number> {
  const res = await api.post<{ delivered: number }>('/api/push/test');
  return res.delivered;
}

async function subscribeWeb(): Promise<boolean> {
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = await navigator.serviceWorker.ready;
  const { key } = await api.get<{ key: string }>('/api/push/vapid');
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
    }));

  const json = sub.toJSON();
  await api.post('/api/push/subscribe', { endpoint: json.endpoint, keys: json.keys });
  return true;
}

async function subscribeNative(): Promise<boolean> {
  const existing = await Notifications.getPermissionsAsync();
  const status = existing.granted ? existing : await Notifications.requestPermissionsAsync();
  if (!status.granted) return false;

  if (Platform.OS === 'android') {
    // Without a channel, Android silently drops every notification this app sends.
    await Notifications.setNotificationChannelAsync('default', {
      name: 'Hunsy',
      importance: Notifications.AndroidImportance.DEFAULT,
    });
  }

  const projectId =
    Constants.expoConfig?.extra?.eas?.projectId ?? (Constants as { easConfig?: { projectId?: string } }).easConfig?.projectId;
  const token = await Notifications.getExpoPushTokenAsync(projectId ? { projectId } : undefined);
  await api.post('/api/push/subscribe', { channel: 'expo', endpoint: token.data });
  return true;
}

/**
 * Registers the service worker that owns web push display and the offline shell.
 *
 * Expo's web export copies `public/` verbatim, so sw.js ships at the root of the same origin the
 * API worker serves — which is what lets it claim every navigation.
 */
export function registerServiceWorker(): void {
  if (!webSupported()) return;
  navigator.serviceWorker.register('/sw.js').catch(() => undefined);
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}
